import dayjs from 'dayjs';
import React, { useContext, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import Handler from '../../../Handler';
import { GoogleMapBox } from '../../../components/basic/GoogleMapBox';
import { ContentContext, GoogleMapContext } from '../../../contexts';

export const FinalViewer = (props) => {
  const { project } = props;
  const { t, i18n } = useTranslation();
  const { methodologyInfo } = useContext(ContentContext);
  const { setAddress } = useContext(GoogleMapContext);
  const isEn = i18n.language === 'en';

  useEffect(() => {
    if (project?.location?.addr1) {
      setAddress(project.location.addr1?.formatted_address);
    }
  }, [project?.location?.addr1]);

  // 프로젝트 데이터 파싱
  const projectData = useMemo(() => {
    if (!project?.project) return null;

    const data = project;
    const location = typeof data.location === 'string' ? Handler.jsonParser(data.location) : data.location;
    const methodology = Handler.getMethodologyBySeq(methodologyInfo, data.project?.methodology?.seq);

    return {
      // 담당자 정보
      name: data.name || '-',
      phone: data.phone || '-',
      department: data.department || '-',
      email: data.email || '-',
      site: data.site || '-',
      fax: data.fax || '-',
      // 프로젝트 정보
      title: data.title || '-',
      area: data.area || '-',
      subArea: data.subArea || '-',
      methodologySeq: data.methodology?.seq,
      methodologyTitle: isEn ? methodology?.titleEn || methodology?.title : methodology?.title,
      purpose: data.purpose || '-',
      content: data.content || '-',
      // 위치 정보
      location: location || {},
      address: data.address || '-',
      isDomestic: data.isDomestic,
      // 날짜
      reductionStartDate: data.reductionStartDate ? dayjs(data.reductionStartDate).format('YYYY.MM.DD') : '-',
      reductionEndDate: data.reductionEndDate ? dayjs(data.reductionEndDate).format('YYYY.MM.DD') : '-',
      // 이미지
      workplaceImages: data.workplaceImages || [],
      beforeImages: data.beforeImages || [],
      afterImages: data.afterImages || [],
      // 탄소 배출량
      reductionBase: data.reductionBase || 0,
      reductionBusiness: data.reductionBusiness || 0,
      reductionLeak: data.reductionLeak || 0,
      reduction: data.reduction || 0,
      // 모니터링
      monitoringType: data.monitoringType || '-',
      monitoringPlan: data.monitoringPlan || '-',
      proofFile: data.proofFile || null,
      // 증빙서류
      evidence: data.evidence || [],
      // 타기관
      isExported: data.isExported || false,
      exportedTitle: data.exportedTitle || '-',
      exportedOrgan: data.exportedOrgan || '-',
      exportedDate: data.exportedDate ? dayjs(data.exportedDate).format('YYYY.MM.DD') : '-',
    };
  }, [project, isEn]);

  console.log('projectData', projectData);

  // 이미지 렌더링
  const renderImages = (images, title) => {
    if (!images || images.length === 0) return null;

    return (
      <div className='mb-6'>
        <h4 className='text-base font-semibold mb-3'>{title}</h4>
        <div className='grid grid-cols-4 gap-4 border border-solid border-gray-200 rounded-md p-4'>
          {images.map((image, index) => (
            <div key={index} className='relative border border-gray-300 rounded-md overflow-hidden h-[150px]'>
              <img
                src={image}
                alt={`${title} ${index + 1}`}
                className='w-full h-full object-cover cursor-pointer'
                onClick={() => window.open(image, '_blank')}
              />
            </div>
          ))}
        </div>
      </div>
    );
  };

  // 증빙서류 렌더링
  const renderEvidence = () => {
    if (!projectData?.evidence || projectData.evidence.length === 0) return null;

    return (
      <div className='mb-6'>
        <h4 className='text-base font-semibold mb-3'>{t('SUPPORTING_DOC')}</h4>
        <div className='grid grid-cols-2 gap-4'>
          {projectData.evidence.map((item, index) => (
            <div key={index} className='border border-gray-200 rounded-lg p-4 bg-white'>
              <div className='mb-2'>
                <span className='text-sm font-medium text-gray-700'>
                  {t('EVIDENCE')} {index + 1}
                </span>
              </div>
              <div className='mb-2'>
                <span className='text-xs text-gray-500'>{t('TYPE')}: </span>
                <span className='text-sm'>{item.type === 'File' ? t('DO_SUBMIT') : t('NOT_DO_SUBMIT')}</span>
              </div>
              {item.type === 'File' && item.data && (
                <div>
                  <a
                    href={item.data}
                    target='_blank'
                    rel='noopener noreferrer'
                    className='text-sm text-blue-600 hover:underline'
                  >
                    {t('VIEW_FILE')}
                  </a>
                </div>
              )}
              {item.type === 'Reason' && item.data && <div className='text-sm text-gray-700'>{item.data}</div>}
            </div>
          ))}
        </div>
      </div>
    );
  };

  if (!projectData) {
    return (
      <article className='flex flex-col gap-4 w-[945px] mb-4 py-8'>
        <header>
          <h2>{t('FINAL')}</h2>
        </header>
        <div className='text-center py-8 text-gray-500'>{t('NO_DATA') || '데이터가 없습니다.'}</div>
      </article>
    );
  }

  return (
    <article className='flex flex-col gap-10 w-[945px] box-border mb-4 py-8'>
      <header>
        <h2 className='text-2xl font-bold'>{t('FINAL')}</h2>
      </header>

      {/* 담당자 정보 */}
      <section className='bg-white border border-gray-200 rounded-lg'>
        <h3 className='text-lg font-semibold mb-4 pb-2 border-b border-gray-200'>{t('APPLICANT')}</h3>
        <div className='grid grid-cols-2 gap-4'>
          <div>
            <span className='text-sm text-gray-600'>{t('CONTACT_NAME')}: </span>
            <span className='text-sm font-medium'>{projectData.name}</span>
          </div>
          <div>
            <span className='text-sm text-gray-600'>{t('MOBILE')}: </span>
            <span className='text-sm font-medium'>{projectData.phone}</span>
          </div>
          <div>
            <span className='text-sm text-gray-600'>{t('DEPARTMENT')}: </span>
            <span className='text-sm font-medium'>{projectData.department}</span>
          </div>
          <div>
            <span className='text-sm text-gray-600'>{t('EMAIL')}: </span>
            <span className='text-sm font-medium'>{projectData.email}</span>
          </div>
        </div>
      </section>

      {/* 프로젝트 정보 */}
      <section className='bg-white border border-gray-200 rounded-lg'>
        <h3 className='text-lg font-semibold mb-4 pb-2 border-b border-gray-200'>{t('BIZ_INFO') || '프로젝트 정보'}</h3>
        <div className='flex flex-col gap-4'>
          <div>
            <span className='text-sm text-gray-600'>{t('PROJECT_NAME') || '프로젝트명'}: </span>
            <span className='text-sm font-medium'>{projectData.title}</span>
          </div>
          <div className='grid grid-cols-2 gap-4'>
            <div>
              <span className='text-sm text-gray-600'>{t('AREA')}: </span>
              <span className='text-sm font-medium'>{t(projectData.area?.toUpperCase()) || projectData.area}</span>
            </div>
            <div>
              <span className='text-sm text-gray-600'>{t('SUB_AREA')}: </span>
              <span className='text-sm font-medium'>
                {t(projectData.subArea?.toUpperCase()) || projectData.subArea}
              </span>
            </div>
          </div>
          <div className='grid grid-cols-2 gap-4'>
            <div>
              <span className='text-sm text-gray-600'>{t('REDUCTION_START_DATE')}: </span>
              <span className='text-sm font-medium'>{projectData.reductionStartDate}</span>
            </div>
            <div>
              <span className='text-sm text-gray-600'>{t('REDUCTION_END_DATE')}: </span>
              <span className='text-sm font-medium'>{projectData.reductionEndDate}</span>
            </div>
          </div>
          <div>
            <span className='text-sm text-gray-600'>{t('METHODOLOGY')}: </span>
            <span className='text-sm font-medium'>{projectData.methodologyTitle}</span>
          </div>
          <div>
            <span className='text-sm text-gray-600'>{t('PURPOSE')}: </span>
            <div
              className='text-sm mt-2 p-3 bg-gray-50 rounded-md'
              dangerouslySetInnerHTML={{ __html: projectData.content }}
            />
          </div>
          <div>
            <span className='text-sm text-gray-600'>{t('CONTENT')}: </span>
            <div
              className='text-sm mt-2 p-3 bg-gray-50 rounded-md'
              dangerouslySetInnerHTML={{ __html: projectData.content }}
            />
          </div>
        </div>
      </section>

      {/* 위치 정보 */}
      <section className='bg-white border border-gray-200 rounded-lg'>
        <h3 className='text-lg font-semibold mb-4 pb-2 border-b border-gray-200'>{t('LOCATION') || '위치 정보'}</h3>
        <div className='flex flex-col gap-2'>
          {projectData.location?.addr1 && (
            <div>
              <span className='text-sm text-gray-600'>{t('ADDRESS')}: </span>
              <span className='text-sm font-medium'>{projectData.location.addr1?.formatted_address}</span>
            </div>
          )}
          <div>
            <span className='text-sm text-gray-600'>{t('ADDRESS_DETAIL')}: </span>
            <span className='text-sm font-medium'>{projectData.location?.addr2 || '-'}</span>
          </div>
        </div>
        <div className='pt-4 rounded-md border border-gray-200 bg-white overflow-hidden'>
          <GoogleMapBox initialAddress={projectData.location.addr1?.formatted_address} />
        </div>
      </section>

      <section className='bg-white border border-gray-200 rounded-lg'>
        <h3 className='text-lg font-semibold mb-4 pb-2 border-b border-gray-200'>
          {t('GHG_REDUCTION') || '탄소 배출량 정보'}
        </h3>
        <div className='grid grid-cols-2 gap-4'>
          <div>
            <span className='text-sm text-gray-600'>{t('BASELINE_EMISSION')}: </span>
            <span className='text-sm font-medium'>
              {Handler.numberWithCommas(projectData.reductionBase)} tCO2-eq / year
            </span>
          </div>
          <div>
            <span className='text-sm text-gray-600'>{t('BIZ_EMISSION')}: </span>
            <span className='text-sm font-medium'>
              {Handler.numberWithCommas(projectData.reductionBusiness)} tCO2-eq / year
            </span>
          </div>
          <div>
            <span className='text-sm text-gray-600'>{t('LEAKAGE')}: </span>
            <span className='text-sm font-medium'>
              {Handler.numberWithCommas(projectData.reductionLeak)} tCO2-eq / year
            </span>
          </div>
          <div>
            <span className='text-sm text-gray-600'>{t('GHG_REDUCTION_QUANTITY')}: </span>
            <span className='text-sm font-medium'>
              {Handler.numberWithCommas(projectData.reduction)} tCO2-eq / year
            </span>
          </div>
        </div>
      </section>

      {/* 모니터링 계획 */}
      <section className='bg-white border border-gray-200 rounded-lg'>
        <h3 className='text-lg font-semibold mb-4 pb-2 border-b border-gray-200'>
          {t('MONITORING_PLAN_INPUT') || '모니터링 계획'}
        </h3>
        <div className='flex flex-col gap-4'>
          <div>
            <span className='text-sm text-gray-600'>{t('MONITORING_PLAN_METHOD')}: </span>
            <span className='text-sm font-medium'>{projectData.monitoringType}</span>
          </div>
          <div>
            <span className='text-sm text-gray-600'>{t('REDUCTION_MONITORING_PLAN_EXP')}: </span>
            <div
              className='text-sm mt-2 p-3 bg-gray-50 rounded-md'
              dangerouslySetInnerHTML={{ __html: projectData.monitoringPlan }}
            />
          </div>
          {projectData.proofFile && (
            <div>
              <span className='text-sm text-gray-600'>{t('SUPPORTING_DOC') || '증명 파일'}: </span>
              <a
                href={projectData.proofFile}
                target='_blank'
                rel='noopener noreferrer'
                className='text-sm text-blue-600 hover:underline ml-2'
              >
                {t('VIEW') || '파일 보기'}
              </a>
            </div>
          )}
        </div>
      </section>

      {/* 이미지 */}
      {renderImages(projectData.workplaceImages, t('BIZ_SITE_PHOTO'))}
      {renderImages(projectData.beforeImages, t('BEFORE_BIZ_IMAGE') || '사전 이미지')}
      {renderImages(projectData.afterImages, t('AFTER_BIZ_IMAGE') || '사후 이미지')}

      {/* 증빙서류 */}
      {renderEvidence()}

      {/* 타기관 정보 */}
      {projectData.isExported && (
        <section className='bg-white border border-gray-200 rounded-lg p-6'>
          <h3 className='text-lg font-semibold mb-4 pb-2 border-b border-gray-200'>
            {t('OTHER_ORG') || '타기관 정보'}
          </h3>
          <div className='grid grid-cols-2 gap-4'>
            <div>
              <span className='text-sm text-gray-600'>{t('OTHER_ORG_NAME') || '타기관명'}: </span>
              <span className='text-sm font-medium'>{projectData.exportedTitle}</span>
            </div>
            <div>
              <span className='text-sm text-gray-600'>{t('REGISTRAR') || '등록자'}: </span>
              <span className='text-sm font-medium'>{projectData.exportedOrgan}</span>
            </div>
            <div>
              <span className='text-sm text-gray-600'>{t('REGISTRATION_DATE') || '등록일'}: </span>
              <span className='text-sm font-medium'>{projectData.exportedDate}</span>
            </div>
          </div>
        </section>
      )}
    </article>
  );
};
